import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';

import {
  attachmentsClear,
  attachmentsDelete,
  attachmentsGetResumes,
  attachmentsLoad,
  getAttachmentsCoverLetters,
  getAttachmentsOthers,
  getAttachmentsResumes,
  getOthersLoaded,
  getResumesLoaded,
} from '@collections/attachments';
import { CandidateSelectedObj } from '@collections/candidates';
import { isModalCancelled, isModalConfirmed, ModalShow } from '@shared/modal';
import { Attachment, CandidateAdapter } from '@shared/models';
import { AssociationModule, AssociationType, AttachmentService } from '@shared/services';
import { isTruthy } from '@shared/utils';

import {
  CandidateAttachmentsConfig,
  CandidateAttachmentsPrivileges,
} from '../../candidate-attachments.abstract.config';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';

@Component({
  selector: 'mojo-candidates-attachments-container',
  templateUrl: './attachments.container.html',
  styleUrls: ['./attachments.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentsContainerComponent implements OnInit, OnDestroy {
  @Input() sideDrawerConfiguration = false;
  candidate$ = this.store.select(CandidateSelectedObj.getCandidatesSelected()).pipe(
    distinctUntilChanged(
      (prev: CandidateAdapter, next: CandidateAdapter) => !!prev && !!next && prev.id === next.id,
    ),
    filter((candidate) => !!candidate && !!candidate.id),
  );

  resumes$: Observable<Attachment[]> = this.candidate$.pipe(
    switchMap((candidate: CandidateAdapter) =>
      this.store.select(getAttachmentsResumes, { candidateId: candidate.id }),
    ),
  );

  others$: Observable<Attachment[]> = this.store.select(getAttachmentsOthers);
  coverLetters$: Observable<Attachment[]> = this.store.select(getAttachmentsCoverLetters);

  resumesLoaded$ = this.store.select(getResumesLoaded);
  othersLoaded$ = this.store.select(getOthersLoaded);

  isConfirmed$ = this.store.select(isModalConfirmed).pipe(filter(isTruthy));
  isCancelled$ = this.store.select(isModalCancelled).pipe(filter(isTruthy));

  confirmSubscription: Subscription;
  cancelSubscription: Subscription;

  emptyMessage = 'No files and attachments';

  modules = AssociationModule;
  types = AssociationType;
  privileges: CandidateAttachmentsPrivileges;

  private subscription = new Subscription();
  coverLetterFE = AppFeatureExtensions.DISPLAY_COVER_LETTER_FILES;

  constructor(
    private store: Store<{}>,
    private candidateAttachmentConfig: CandidateAttachmentsConfig,
    private router: Router,
  ) {
    this.privileges = this.candidateAttachmentConfig.privileges;
  }

  ngOnInit() {
    this.subscription.add(this.candidate$.subscribe((candidate) => this.load(candidate)));
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  load(candidate: CandidateAdapter) {
    this.store.dispatch(attachmentsClear());
    this.store.dispatch(
      attachmentsGetResumes({
        candidateId: candidate.id,
      }),
    );
    this.store.dispatch(
      attachmentsLoad({
        payload: {
          associationType: AttachmentService.AssociationType.OTHERS,
          associationModule: AttachmentService.AssociationModule.CANDIDATE,
          associateModuleId: candidate.id,
        },
      }),
    );

    this.store.dispatch(
      attachmentsLoad({
        payload: {
          associationType: AttachmentService.AssociationType.COVER_LETTER,
          associationModule: AttachmentService.AssociationModule.CANDIDATE,
          associateModuleId: candidate.id,
        },
      }),
    );
  }

  delete(id: number) {
    this.confirmSubscription = this.isConfirmed$.subscribe(() => {
      this.unsubscribe();
      this.store.dispatch(attachmentsDelete({ attachmentId: id }));
    });
    this.cancelSubscription = this.isCancelled$.subscribe(() => {
      this.unsubscribe();
    });
    this.store.dispatch(
      new ModalShow({
        title: 'Are you sure you want to delete this file?',
        subTitle: 'Once deleted, the file will no longer be accessible.',
      }),
    );
  }

  uploadResume() {
    this.router.navigate([{ outlets: { modal: ['attachments'] } }], {
      queryParams: { module: this.modules.CANDIDATE, type: this.types.RESUME },
      skipLocationChange: true,
    });
  }

  uploadOther() {
    this.router.navigate([{ outlets: { modal: ['attachments'] } }], {
      queryParams: { module: this.modules.CANDIDATE, type: this.types.OTHERS },
      skipLocationChange: true,
    });
  }

  unsubscribe() {
    if (!!this.confirmSubscription) {
      this.confirmSubscription.unsubscribe();
    }
    if (!!this.cancelSubscription) {
      this.cancelSubscription.unsubscribe();
    }
  }
}
